import { useContext, useState, useEffect, useRef } from 'react';
import handleChange from '../../../utils/handleChange';
import './register.css';
import phoneValid from '../../../utils/phoneValid';
import { registerFun, regOTPFun, reSendOtp } from '../../../api/userLoginAPI';
import { ReactComponent as CloseIcon } from '../../../assets/icons/trvto_close.svg';
import errorHandle from '../../../utils/errorHandle';
import { toast } from 'react-toastify';
import OTPInput from '../../otpinput/otpinput';
import { UserAuthContext } from '../../../context/UserAuthContext';
import { Link } from 'react-router-dom';

const RESEND_DURATION = 60; // seconds
const OTP_EXPIRY_DURATION = 3 * 60 * 1000; // 3 minutes in ms

export default function Register(props) {
    const { setLogButton, setLoginSec } = props;
    const { login } = useContext(UserAuthContext);

    const [userData, setUserData] = useState({
        name: '',
        countryCode: '+91',
        phoneNumber: '',
        phoneOTP: ''
    });
    const [phoneError, setPhoneError] = useState('');
    const [otpError, setOtpError] = useState('');

    // 'initial' | 'sent'
    const [otpState, setOtpState] = useState('initial');
    const [otpExpired, setOtpExpired] = useState(false);
    const [resendTimer, setResendTimer] = useState(0);

    // Refs so closures always see the latest values
    const resendIntervalRef = useRef(null);
    const otpExpiryTimeoutRef = useRef(null);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            clearInterval(resendIntervalRef.current);
            clearTimeout(otpExpiryTimeoutRef.current);
        };
    }, []);

    function close() {
        setLogButton(false);
        setLoginSec('login');
    }

    function stopAllTimers() {
        clearInterval(resendIntervalRef.current);
        resendIntervalRef.current = null;
        clearTimeout(otpExpiryTimeoutRef.current);
        otpExpiryTimeoutRef.current = null;
        setResendTimer(0);
    }

    function startResendTimer(duration = RESEND_DURATION) {
        clearInterval(resendIntervalRef.current);
        setResendTimer(duration);

        resendIntervalRef.current = setInterval(() => {
            setResendTimer((prev) => {
                if (prev <= 1) {
                    clearInterval(resendIntervalRef.current);
                    resendIntervalRef.current = null;
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }

    function scheduleOtpExpiry() {
        clearTimeout(otpExpiryTimeoutRef.current);

        otpExpiryTimeoutRef.current = setTimeout(() => {
            clearInterval(resendIntervalRef.current);
            resendIntervalRef.current = null;
            setResendTimer(0);

            setOtpExpired(true);
            setOtpState('initial'); // hide OTP input
            setOtpError('OTP has expired. Please generate a new one.');
        }, OTP_EXPIRY_DURATION);
    }

    async function sendOTP(method = 'phone', type) {
        if (!phoneValid(userData.phoneNumber)) {
            setPhoneError('Please enter a valid phone number');
            return;
        }

        setPhoneError('');
        props.setLoading(true);

        try {
            if (type === 'resend') {
                await reSendOtp({ ...userData, age: Number(userData.age) }, 'register', method);
            } else {
                await regOTPFun({ ...userData, age: Number(userData.age) }, method);
            }

            toast.success(`OTP sent to your WhatsApp: ${userData.phoneNumber}`);
            setOtpState('sent');
            setOtpExpired(false);
            setOtpError('');
            // Clear OTP field on resend
            setUserData((prev) => ({ ...prev, phoneOTP: '' }));

            startResendTimer(RESEND_DURATION);
            scheduleOtpExpiry();
        } catch (error) {
            errorHandle(error);
        } finally {
            props.setLoading(false);
        }
    }

    async function handleSignUp() {
        if (userData.phoneOTP.length < 6) {
            const msg = 'Please enter the complete OTP';
            toast.error(msg);
            setOtpError(msg);
            return;
        }

        setOtpError('');
        props.setLoading(true);

        try {
            const res = await registerFun(userData);
            toast.success('Account created successfully. You are now logged in!');
            stopAllTimers();
            login(res.data);
            close();
        } catch (error) {
            errorHandle(error);
        } finally {
            props.setLoading(false);
        }
    }

    function handleMainButtonClick() {
        if (otpState === 'initial' || otpExpired) {
            sendOTP('phone', 'register');
        } else {
            handleSignUp();
        }
    }

    const formatTimer = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = String(seconds % 60).padStart(2, '0');
        return `${m}:${s}`;
    };

    const mainButtonLabel = otpState === 'initial' ? 'Confirm' : otpExpired ? 'Generate New OTP' : 'Sign Up';

    return (
        <div className='reg'>
            <h1 className='regH1'>{otpState === 'initial' ? 'Sign Up' : 'OTP Verification'}</h1>
            <button type='button' onClick={close} className='regCancel'>
                <CloseIcon className='regCloseIcon' />
            </button>

            <div className='regInputDiv'>
                <label>Name</label>
                <input
                    placeholder='Name'
                    name='name'
                    onChange={(e) => handleChange(e, setUserData)}
                    value={userData.name}
                    className='regInput'
                />
                <label>Phone Number (WhatsApp)</label>
                <input
                    placeholder='Number'
                    type='text'
                    name='phoneNumber'
                    onChange={(e) => handleChange(e, setUserData)}
                    value={userData.phoneNumber}
                    className='regInput'
                />
                <label>Gender</label>
                <select
                    value={userData.gender}
                    name='gender'
                    onChange={(e) => handleChange(e, setUserData)}
                    className='regInput'
                >
                    <option value='Select'>Select</option>
                    <option value='Male'>Male</option>
                    <option value='Female'>Female</option>
                    <option value='Others'>Others</option>
                </select>
                <label>Age</label>
                <input
                    placeholder='Age'
                    name='age'
                    type='number'
                    onChange={(e) => handleChange(e, setUserData)}
                    value={userData.age}
                    className='regInput'
                />

                {phoneError && <span className='regError'>{phoneError}</span>}

                {otpState === 'sent' && (
                    <div className='regInputDiv'>
                        <p>
                            Verification code has been sent to your WhatsApp {userData.phoneNumber}, please enter it
                            here to complete signup.
                        </p>
                        <label>Phone OTP</label>
                        <OTPInput length={6} setUserData={setUserData} keyName='phoneOTP' />
                    </div>
                )}

                {otpState === 'sent' && !otpExpired && (
                    <div className='resend-container'>
                        {resendTimer > 0 ? (
                            <p className='resend-timer'>Resend OTP in {formatTimer(resendTimer)}</p>
                        ) : (
                            <button
                                className='resend-btn'
                                onClick={() => sendOTP('phone', 'resend')}
                                disabled={props.loading}
                            >
                                Resend OTP
                            </button>
                        )}
                    </div>
                )}

                {otpError && <span className='regError'>{otpError}</span>}
            </div>

            <button
                type='button'
                onClick={handleMainButtonClick}
                className='regBtn'
                disabled={userData.phoneNumber.length !== 10 || props.loading}
            >
                {mainButtonLabel}
            </button>

            <h2 onClick={() => setLoginSec('login')} className='regForgot'>
                Login
            </h2>
            <p className='agreeTerms'>
                By continuing you agree to our <Link to='/privacy-policy'>Privacy Policy</Link> and{' '}
                <Link to='/terms'>Terms of Use</Link>
            </p>
        </div>
    );
}
